/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import type { CustomLegend } from 'lumen/components/hotel/benchmark-insight/charts/legend-items/legend-item';
import HotelBenchmarkInsightChartsLegendItemsLegendItem from 'lumen/components/hotel/benchmark-insight/charts/legend-items/legend-item';
import type { BeniTooltipData } from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import HotelBenchmarkInsightTooltipsBeniTooltip from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import HotelBenchmarkInsightTooltipsSharedTooltipMetricBlock from 'lumen/components/hotel/benchmark-insight/tooltips/shared/tooltip-metric-block';
import type { BeniBreakdown, BeniMetaComparisonSet } from 'lumen/models';
import { BREAKDOWN_PERIOD_TYPES } from 'lumen/models/benchmark-insight/breakdown';
import getElement from 'lumen/modifiers/get-element';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  getClosestWeekRange,
  getWeekendPlotBands,
  tickPositionerPartialWeeks,
} from 'lumen/utils/charts/chart-utils';
import { addZone, intersection } from 'lumen/utils/charts/zones';
import {
  BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS,
  BENI_CHARTS_GRID_LINE_COLOR,
  BENI_CHARTS_HEIGHT,
  BENI_CHARTS_LABEL_COLOR,
  BENI_CHARTS_Y_AXIS_LABEL_MIN_WIDTH,
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  COLOR_BENCHMARK_INSIGHT_DATASET_OWN,
  COLOR_BENCHMARK_INSIGHT_DATASET_PRIMARY,
  COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_OWN,
  COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_PRIMARY,
  COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_SECONDARY,
  COLOR_BENCHMARK_INSIGHT_DATASET_SECONDARY,
  type HeroChartAxisOption,
  HIDDEN_FUTURE_COMPSET_DATA_TRANSLATIONS,
  type Kpi,
  KPI_INDEX_INFO_TOOLTIP_TRANSLATIONS,
  KPI_INDEX_LONG_AND_ABBREVIATED_TRANSLATIONS,
  KPI_INDEX_LONG_TRANSLATIONS,
  KPI_INDEX_TRANSLATIONS,
  KPI_INDEXES_FOR_KPIS,
  KPI_INDEXOPTION,
  KPIS,
  NUMBER_FORMATS,
  NUMBER_STYLES,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import {
  HERO_CHART_SERIES_IDS,
  type HeroChartKpiDiffVsComparisonSetSeriesPoint,
  type HeroChartSeries,
  type HeroChartSeriesId,
} from 'lumen/utils/constants/benchmark-insight/beni-hero-chart-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import { BENI_TABLE_ROW_TYPES } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import {
  DATE_AGGREGATION,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';

const columnSeriesOptions = {
  pointWidth: undefined, // Dynamic column width dependent on the number of data points.
  minPointLength: 2, // Set a minimum length for columns, so that zero values will still show a tiny column, instead of nothing at all.
  borderWidth: 0,
};

// Add a custom column type to Highcharts, to use for the reference period series.
// This is necessary for the chart to view it as a separate series.
// With this solution, we can layer 2 columns of different types on top of eachother.
// Without this, reference period columns will be placed alongside regular columns.
const REFERENCE_PERIOD_COLUMN = 'reference_period_column';

Highcharts.seriesType(REFERENCE_PERIOD_COLUMN, 'column', columnSeriesOptions);

interface HotelBenchmarkInsightChartsHeroChartSignature {
  Args: {
    dateAggregation: DateAggregationType;
    isOccupancySelected: boolean;
    kpiDiffVsComparisonSetTranslation: string;
    ownComparisonSetBreakdowns: BeniBreakdown[];
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    pickupTranslation: string;
    primaryComparisonSetBreakdowns: BeniBreakdown[];
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    rangeChanged: (dateRange: DateRangeType) => void;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetBreakdowns: BeniBreakdown[];
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    selectedAxisOption: HeroChartAxisOption;
    selectedKpi: Kpi;
    selectedRange: DateRangeType;
  };
}

export default class HotelBenchmarkInsightChartsHeroChart extends Component<HotelBenchmarkInsightChartsHeroChartSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-chart-service')
  declare benchmarkInsightBeniChartService: Services['benchmark-insight/beni-chart-service'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];

  // eslint-disable-next-line ember/no-unused-services
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked dateRange: DateRangeType | null = null;
  @tracked tooltipData: BeniTooltipData | null = null;
  @tracked visibleSeries: HeroChartSeriesId[] = Object.values(
    HERO_CHART_SERIES_IDS,
  );
  @tracked warningIconElement: HTMLElement | null = null;

  TICK_AMOUNT = 6;
  TITLE_NAME = '';
  TOOLTIP_DUMMY_ELEMENT_ID = 'tooltip-hero-chart-dummy-div';

  visibleKpis: Kpi[] = [KPIS.OCCUPANCY, KPIS.ADR, KPIS.REVPAR];

  get chartData(): HeroChartSeries[] {
    const {
      selectedKpi,
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;
    const {
      ownComparisonSetBreakdownsWithoutTotal,
      primaryComparisonSetBreakdownsWithoutTotal,
      secondaryComparisonSetBreakdownsWithoutTotal,
    } = this;
    const primaryComparisonSetType =
      primaryComparisonSetMeta?.comparisonSetType;

    const disabledHoverProperties = {
      marker: {
        enabled: false,
      },
      states: {
        hover: {
          enabled: false,
        },
      },
    };

    const ownComparisonSetSeries: HeroChartSeries[] =
      this.getChartDataSeriesByComparisonSet(
        ownComparisonSetMeta,
        HERO_CHART_SERIES_IDS.OWN,
        HERO_CHART_SERIES_IDS.OWN_REFERENCE,
        COLOR_BENCHMARK_INSIGHT_DATASET_OWN,
        COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_OWN,
      );

    const primaryComparisonSetSeries: HeroChartSeries[] =
      this.getChartDataSeriesByComparisonSet(
        primaryComparisonSetMeta,
        HERO_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET,
        HERO_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET_REFERENCE,
        COLOR_BENCHMARK_INSIGHT_DATASET_PRIMARY,
        COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_PRIMARY,
      );

    const secondaryComparisonSetSeries: HeroChartSeries[] =
      this.getChartDataSeriesByComparisonSet(
        secondaryComparisonSetMeta,
        HERO_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET,
        HERO_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET_REFERENCE,
        COLOR_BENCHMARK_INSIGHT_DATASET_SECONDARY,
        COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_SECONDARY,
      );

    const kpiDiffVsComparisonSetSeries: HeroChartSeries = {
      name: this.args.kpiDiffVsComparisonSetTranslation,
      id: HERO_CHART_SERIES_IDS.KPI_DIFF_VS_COMPARISON_SET,
      data: [],
      yAxis: 1,
      type: 'arearange',
      zones: [],
      zoneAxis: 'x',
      zIndex: -1,
      visible: this.visibleSeries.includes(
        HERO_CHART_SERIES_IDS.KPI_DIFF_VS_COMPARISON_SET,
      ),
      ...disabledHoverProperties,
      color: {
        linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
        stops: [
          [0.5, 'var(--prism-color-general-success-100)'],
          [0.5, 'var(--prism-color-general-critical-100)'],
        ],
      },
    };

    // The border ghost series don't need IDs, because they're not being tracked for usage tracking.
    const kpiDiffVsComparisonSetTopBorderGhostSeries: HeroChartSeries = {
      name: 'kpiDiffVsComparisonSetTopBorderGhostSeries',
      type: 'line',
      data: [],
      yAxis: 1,
      color: COLOR_BENCHMARK_INSIGHT_DATASET_OWN,
      lineWidth: 1,
      zIndex: 0,
      visible: this.visibleSeries.includes(
        HERO_CHART_SERIES_IDS.KPI_DIFF_VS_COMPARISON_SET,
      ),
      ...disabledHoverProperties,
    };

    // The border ghost series don't need IDs, because they're not being tracked for usage tracking.
    const kpiDiffVsComparisonSetBottomBorderGhostSeries: HeroChartSeries = {
      name: 'kpiDiffVsComparisonSetBottomBorderGhostSeries',
      type: 'line',
      data: [],
      yAxis: 1,
      color: primaryComparisonSetType
        ? COLOR_BENCHMARK_INSIGHT_DATASET_PRIMARY
        : '',
      lineWidth: 1,
      zIndex: 0,
      visible: this.visibleSeries.includes(
        HERO_CHART_SERIES_IDS.KPI_DIFF_VS_COMPARISON_SET,
      ),
      ...disabledHoverProperties,
    };

    const indexSeries: HeroChartSeries = {
      type: 'line',
      name: this.intl.t(
        KPI_INDEX_TRANSLATIONS[KPI_INDEXES_FOR_KPIS[selectedKpi]],
      ),
      id: HERO_CHART_SERIES_IDS.INDEX,
      data: [],
      yAxis: 1,
      color: 'var(--prism-color-general-success-200)',
      threshold: 1, // Value to determine color change
      negativeColor: 'var(--prism-color-general-critical-200)',
      zIndex: -1,
      visible: this.visibleSeries.includes(HERO_CHART_SERIES_IDS.INDEX),
      ...disabledHoverProperties,
    };

    const series = [];

    // We should only iterate over segmentations' ref objects if the comparison set has reference period data.
    // The ref objects are only present when the comparison set has reference period data.
    const hasReferencePeriodDataFilter = (
      comparisonSetSeries: HeroChartSeries,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ) => {
      if (
        comparisonSetSeries.breakdownPeriodType ===
        BREAKDOWN_PERIOD_TYPES.REFERENCE
      ) {
        return comparisonSetSeries.hasReferencePeriodData;
      }

      return true;
    };

    let isLastZonePositive = false;
    let previousKpiDiffVsComparisonSetPoint: HeroChartKpiDiffVsComparisonSetSeriesPoint | null =
      null;

    ownComparisonSetBreakdownsWithoutTotal.forEach((ownSegmentation, index) => {
      const xValueSegmentation = this.getXValueForSegmentation(ownSegmentation);
      const showWarningForWeek =
        this.shouldShowWarningForWeek(xValueSegmentation);

      ownComparisonSetSeries
        .filter(hasReferencePeriodDataFilter)
        .forEach((comparisonSetSeries) => {
          let breakdownPeriod = null;

          if (comparisonSetSeries.breakdownPeriodType) {
            breakdownPeriod =
              ownSegmentation.getBreakdownPeriodByBreakdownPeriodType(
                comparisonSetSeries.breakdownPeriodType,
              );
          }

          comparisonSetSeries.data.push({
            x: xValueSegmentation,
            y: breakdownPeriod?.total[selectedKpi] ?? null,
            segmentation: ownSegmentation,
            showWarningForWeek,
          });
        });

      // Check that there is a reference comparison set to show, otherwise we shouldn't add the kpiDiffVsReference series or index series.
      if (primaryComparisonSetType) {
        if (this.args.selectedAxisOption === KPI_INDEXOPTION.KPI_DIFFERENCE) {
          const zoneParameters = this.addZoneIntersections(
            kpiDiffVsComparisonSetSeries,
            kpiDiffVsComparisonSetTopBorderGhostSeries,
            kpiDiffVsComparisonSetBottomBorderGhostSeries,
            ownSegmentation,
            xValueSegmentation,
            index,
            isLastZonePositive,
            previousKpiDiffVsComparisonSetPoint,
          );
          isLastZonePositive =
            zoneParameters.isLastZonePositiveForThisSegmentation;
          previousKpiDiffVsComparisonSetPoint =
            zoneParameters.previousKpiDiffVsComparisonSetPoint;
        } else {
          const matchingPrimaryComparisonSetBreakdown =
            primaryComparisonSetBreakdownsWithoutTotal.find(
              (primaryComparisonSetBreakdown) =>
                primaryComparisonSetBreakdown.label === ownSegmentation.label,
            ) ?? null;

          if (matchingPrimaryComparisonSetBreakdown) {
            indexSeries.data.push({
              x: this.getXValueForSegmentation(
                matchingPrimaryComparisonSetBreakdown,
              ),
              y:
                matchingPrimaryComparisonSetBreakdown?.current.total.index[
                  KPI_INDEXES_FOR_KPIS[selectedKpi]
                ] ?? null,
            });
          }
        }
      }
    });

    if (ownComparisonSetMeta?.isVisible) {
      ownComparisonSetSeries.forEach((comparisonSetSeries) => {
        series.push(comparisonSetSeries);
      });
    }

    primaryComparisonSetBreakdownsWithoutTotal.forEach(
      (primaryComparisonSetBreakdown) => {
        primaryComparisonSetSeries
          .filter(hasReferencePeriodDataFilter)
          .forEach((comparisonSetSeries) => {
            const x = this.getXValueForSegmentation(
              primaryComparisonSetBreakdown,
            );

            if (comparisonSetSeries.breakdownPeriodType) {
              const breakdownPeriod =
                primaryComparisonSetBreakdown.getBreakdownPeriodByBreakdownPeriodType(
                  comparisonSetSeries.breakdownPeriodType,
                );

              const point = {
                x,
                y: breakdownPeriod.total[selectedKpi],
                segmentation: primaryComparisonSetBreakdown,
              };

              comparisonSetSeries.data.push(point);
            }
          });
      },
    );

    if (primaryComparisonSetMeta?.isVisible) {
      primaryComparisonSetSeries.forEach((comparisonSetSeries) => {
        series.push(comparisonSetSeries);
      });
    }

    secondaryComparisonSetBreakdownsWithoutTotal.forEach(
      (secondaryComparisonSetBreakdown) => {
        secondaryComparisonSetSeries
          .filter(hasReferencePeriodDataFilter)
          .forEach((comparisonSetSeries) => {
            const x = this.getXValueForSegmentation(
              secondaryComparisonSetBreakdown,
            );

            if (comparisonSetSeries.breakdownPeriodType) {
              const breakdownPeriod =
                secondaryComparisonSetBreakdown.getBreakdownPeriodByBreakdownPeriodType(
                  comparisonSetSeries.breakdownPeriodType,
                );

              const point = {
                x,
                y: breakdownPeriod.total[selectedKpi],
                segmentation: secondaryComparisonSetBreakdown,
              };

              comparisonSetSeries.data.push(point);
            }
          });
      },
    );

    if (secondaryComparisonSetMeta?.isVisible) {
      secondaryComparisonSetSeries.forEach((comparisonSetSeries) => {
        series.push(comparisonSetSeries);
      });
    }

    // Check that there is a preferred comparison set to show, otherwise we shouldn't add the kpiDiffVsComparisonSet series or index series.
    if (primaryComparisonSetMeta?.isVisible) {
      if (this.args.selectedAxisOption === KPI_INDEXOPTION.KPI_DIFFERENCE) {
        // Add last zone
        const ownLast =
          ownComparisonSetBreakdownsWithoutTotal[
            ownComparisonSetBreakdownsWithoutTotal.length - 1
          ];

        if (kpiDiffVsComparisonSetSeries.zones && ownLast) {
          addZone(
            kpiDiffVsComparisonSetSeries.zones,
            this.getXValueForSegmentation(ownLast),
            {
              color: !isLastZonePositive
                ? 'var(--prism-color-general-critical-100)'
                : 'var(--prism-color-general-success-100)',
            },
          );
        }

        series.push(kpiDiffVsComparisonSetTopBorderGhostSeries);
        series.push(kpiDiffVsComparisonSetBottomBorderGhostSeries);
        series.push(kpiDiffVsComparisonSetSeries);
      } else {
        series.push(indexSeries);
      }
    }

    return series;
  }

  get chartOptions(): Highcharts.Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        type: 'column',
        height: BENI_CHARTS_HEIGHT,
        zoomType: 'x',
        resetZoomButton: {
          theme: {
            display: 'none',
          },
        },
        spacingLeft: 30, // Add spacingLeft to make space for the custom axis title
        spacingRight: 30,
        // Don't make visible series take up the space from invisible series.
        // Problem: reference series are on a separate X axis, in order for us to visualize them behind regular series.
        // By doing that, we keep the benefit of Highcharts automatically calculating the column width.
        // However, when the user toggles off a reference series without toggling off the accompanying regular series, the columns aren't aligned anymore.
        // For example: My hotel (turned on) and My hotel STLY (turned off).
        // Switching off this ignoreHiddenSeries setting is a quick fix for that problem.
        ignoreHiddenSeries: false,
        animation: false,
      },
      yAxis: this.yAxis,
      xAxis: this.xAxis,
      tooltip: {
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        outside: true,
        shared: true,
        useHTML: true,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter(this: Highcharts.TooltipFormatterContextObject) {
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          that.tooltipData = that.tooltipDataDict[this.x] ?? null;
          end();

          return (
            document.getElementById(that.TOOLTIP_DUMMY_ELEMENT_ID)?.innerHTML ??
            null
          );
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: columnSeriesOptions,
      },
    };
  }

  get comparisonSetsHaveReferencePeriodData(): boolean {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;

    return Boolean(
      ownComparisonSetMeta?.hasReferencePeriodData ||
      primaryComparisonSetMeta?.hasReferencePeriodData ||
      secondaryComparisonSetMeta?.hasReferencePeriodData,
    );
  }

  get ownComparisonSetBreakdownsWithoutTotal(): BeniBreakdown[] {
    return this.filterBreakdowns(this.args.ownComparisonSetBreakdowns);
  }

  get plotLineKpiIndex(): Highcharts.AxisPlotLinesOptions {
    return {
      // KPI index values above 100 are positive, KPI index values below 100 are negative.
      // Chart data is between 0 and 1, so use a float for the same scale.
      value: 1,
      dashStyle: 'Dash',
      color: 'var(--prism-color-chart-sequential-neutral-05)',
      width: 1,
      zIndex: 1,
    };
  }

  get primaryComparisonSetBreakdownsWithoutTotal(): BeniBreakdown[] {
    return this.filterBreakdowns(this.args.primaryComparisonSetBreakdowns);
  }

  get secondaryComparisonSetBreakdownsWithoutTotal(): BeniBreakdown[] {
    return this.filterBreakdowns(this.args.secondaryComparisonSetBreakdowns);
  }

  // Memoization of the tooltip data.
  // Instead of recalculating the tooltip data in the tooltip formatter function, we calculate it once and store it in this dict.
  // The cached decorator is needed to make sure the getter is not recalculated on hover, as this greatly reduces performance.
  @cached
  get tooltipDataDict(): Record<number, BeniTooltipData> {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;

    const dict: Record<number, BeniTooltipData> = {};

    if (ownComparisonSetMeta) {
      this.addComparisonSetBreakdownsToTooltipDataDict(
        dict,
        ownComparisonSetMeta,
        'ownComparisonSetBreakdown',
      );
    }

    if (primaryComparisonSetMeta) {
      this.addComparisonSetBreakdownsToTooltipDataDict(
        dict,
        primaryComparisonSetMeta,
        'primaryComparisonSetBreakdown',
      );
    }

    if (secondaryComparisonSetMeta) {
      this.addComparisonSetBreakdownsToTooltipDataDict(
        dict,
        secondaryComparisonSetMeta,
        'secondaryComparisonSetBreakdown',
      );
    }

    return dict;
  }

  get visibleCustomLegends(): CustomLegend[] {
    const {
      selectedKpi,
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;
    const visibleCustomLegends: CustomLegend[] = [];

    if (ownComparisonSetMeta) {
      const ownHotelCustomLegends = this.getCustomLegendsByComparisonSet(
        ownComparisonSetMeta,
        HERO_CHART_SERIES_IDS.OWN,
        HERO_CHART_SERIES_IDS.OWN_REFERENCE,
        BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.OWN,
        BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.OWN_REFERENCE,
      );

      visibleCustomLegends.push(...ownHotelCustomLegends);
    }

    if (primaryComparisonSetMeta) {
      const primaryComparisonSetCustomLegends =
        this.getCustomLegendsByComparisonSet(
          primaryComparisonSetMeta,
          HERO_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET,
          HERO_CHART_SERIES_IDS.PRIMARY_COMPARISON_SET_REFERENCE,
          BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.PRIMARY,
          BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.PRIMARY_REFERENCE,
        );

      visibleCustomLegends.push(...primaryComparisonSetCustomLegends);
    }

    if (secondaryComparisonSetMeta) {
      const secondaryComparisonSetCustomLegends =
        this.getCustomLegendsByComparisonSet(
          secondaryComparisonSetMeta,
          HERO_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET,
          HERO_CHART_SERIES_IDS.SECONDARY_COMPARISON_SET_REFERENCE,
          BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.SECONDARY,
          BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS.SECONDARY_REFERENCE,
        );

      visibleCustomLegends.push(...secondaryComparisonSetCustomLegends);
    }

    if (primaryComparisonSetMeta) {
      const primaryComparisonSetType =
        primaryComparisonSetMeta.comparisonSetType;

      const isDisabledForUnavailableComparisonSet =
        !ownComparisonSetMeta?.isVisible ||
        !primaryComparisonSetMeta?.isVisible;

      if (this.args.selectedAxisOption === KPI_INDEXOPTION.KPI_DIFFERENCE) {
        visibleCustomLegends.push({
          id: HERO_CHART_SERIES_IDS.KPI_DIFF_VS_COMPARISON_SET,
          cssSelector: 'kpi-diff-vs-comparison-set',
          type: 'dot-large',
          isHidden: !this.visibleSeries.includes(
            HERO_CHART_SERIES_IDS.KPI_DIFF_VS_COMPARISON_SET,
          ),
          label: this.args.kpiDiffVsComparisonSetTranslation,
          isDisabled: isDisabledForUnavailableComparisonSet,
          isDisabledForUnavailableComparisonSet,
        });
      } else {
        const isDisabledForHiddenFutureCompsetData =
          this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
            primaryComparisonSetType,
            this.args.selectedRange.start,
            selectedKpi,
          );

        visibleCustomLegends.push({
          id: HERO_CHART_SERIES_IDS.INDEX,
          cssSelector: 'index',
          type: 'line',
          isHidden: !this.visibleSeries.includes(HERO_CHART_SERIES_IDS.INDEX),
          isDisabled:
            isDisabledForHiddenFutureCompsetData ||
            isDisabledForUnavailableComparisonSet,
          isDisabledForHiddenFutureCompsetData,
          isDisabledForUnavailableComparisonSet,
          label: this.intl.t(
            KPI_INDEX_LONG_AND_ABBREVIATED_TRANSLATIONS[
              KPI_INDEXES_FOR_KPIS[selectedKpi]
            ],
          ),
        });
      }
    }

    // Add info icon to the index legend.
    const indexLegend = visibleCustomLegends.find(
      (legend) => legend.id === HERO_CHART_SERIES_IDS.INDEX,
    );

    if (indexLegend && primaryComparisonSetMeta) {
      indexLegend.infoTooltipContent = {
        title: this.intl.t(
          KPI_INDEX_LONG_TRANSLATIONS[KPI_INDEXES_FOR_KPIS[selectedKpi]],
        ),
        prefix: this.intl.t(
          KPI_INDEX_INFO_TOOLTIP_TRANSLATIONS[
            KPI_INDEXES_FOR_KPIS[selectedKpi]
          ],
          {
            comparisonSet: this.intl.t(
              BENI_COMPARISON_SET_LONG_TRANSLATIONS[
                primaryComparisonSetMeta.comparisonSetType
              ],
            ),
          },
        ),
      };
    }

    this.benchmarkInsightBeniService.addUnavailableComparisonSetDisabledTooltip(
      visibleCustomLegends,
    );

    // Add info tooltip to legends that are disabled for hidden future compset data.
    const legendsDisabledForHiddenFutureCompsetData =
      visibleCustomLegends.filter(
        (legend) => legend.isDisabledForHiddenFutureCompsetData,
      );

    legendsDisabledForHiddenFutureCompsetData.forEach((legend) => {
      let disabledTooltipTranslationKey =
        HIDDEN_FUTURE_COMPSET_DATA_TRANSLATIONS[selectedKpi];

      if (legend.id === HERO_CHART_SERIES_IDS.INDEX) {
        disabledTooltipTranslationKey =
          'benchmark_insight.hidden_future_data.button_tooltip.rgi';
      }

      if (disabledTooltipTranslationKey) {
        legend.disabledTooltipText = this.intl.t(disabledTooltipTranslationKey);
      }
    });

    return visibleCustomLegends;
  }

  get weekendPlotBands(): Highcharts.XAxisPlotBandsOptions[] {
    const startDate = this.args.selectedRange.start;
    const endDate = this.args.selectedRange.end;

    return getWeekendPlotBands(this.hotelAndMarketService, startDate, endDate);
  }

  get xAxis(): Highcharts.XAxisOptions[] {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const axisType: Highcharts.AxisTypeValue = 'datetime';

    const xAxis = [
      {
        type: axisType,
        plotBands:
          this.args.dateAggregation === DATE_AGGREGATION.DAY
            ? this.weekendPlotBands
            : undefined, // Add weekend plot bands if aggregated by day.
        labels: {
          style: {
            color: BENI_CHARTS_LABEL_COLOR,
          },
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            const value = this.value as number;

            if (!value) {
              return '';
            }

            const dateFormatted = Highcharts.dateFormat(
              that.dates.highchartsLabelFormats[that.args.dateAggregation],
              value,
            );

            if (
              dateFormatted &&
              that.args.dateAggregation === DATE_AGGREGATION.WEEK
            ) {
              let showWarningForWeek = false;
              let warningSign = '';
              if (this.isFirst || this.isLast) {
                showWarningForWeek = that.shouldShowWarningForWeek(value);
                const warningIcon = that.warningIconElement?.innerHTML ?? '';
                if (showWarningForWeek) {
                  warningSign = warningIcon;
                }
              }

              const weekDateFormatted = that.intl.t('general.week_of', {
                date: dateFormatted,
              });
              return [
                `<div class="x-axis-label-with-icon">`,
                `  <div>${weekDateFormatted}</div>`,
                `  ${warningSign}`,
                `</div>`,
              ].join('\n');
            }

            return dateFormatted;
          },
          useHTML: true,
        },
        tickPositioner(
          this: Highcharts.Axis,
        ): Highcharts.AxisTickPositionsArray {
          return tickPositionerPartialWeeks(this.series, 5);
        },
        events: {
          setExtremes(e: Highcharts.AxisSetExtremesEventObject): void {
            /*
          We use setExtremes to capture the correct min and max values.
          setExtremes executes the moment the user lets go of the zoom
          so we get the exact min and max values the user selected and save it in dateRange variable.
          */
            if (e.trigger && e.trigger === 'zoom') {
              const dayjsStart = dayjs.utc(e.min);
              const dayjsEnd = dayjs.utc(e.max);

              let startDate = dayjsStart;
              const endDate = dayjsEnd;

              // Highcharts always seems to select 1 day before the desired start date, fix this by adding 1 day.
              // Only do this when the start date is not the same as the end date, otherwise we end up with a negative date range.
              if (!startDate.isSame(endDate, 'day')) {
                startDate = startDate.add(1, 'day');
              }
              // return full week based on start date
              const isWeekView =
                that.args.dateAggregation === DATE_AGGREGATION.WEEK;

              if (isWeekView) {
                that.dateRange = getClosestWeekRange(startDate, endDate);
              } else {
                that.dateRange = {
                  start: startDate,
                  end: endDate,
                };
              }
            }
          },
          afterSetExtremes(e: Highcharts.AxisSetExtremesEventObject): void {
            /*
          We need to use afterSetExtremes to execute the rangeChanged args method because it runs after the chart has redrawn and help us avoid unexpected errors if we change chart options during a redraw.
          We need to check the trigger of the event, since this is not only after a zoom
          */
            if (e.trigger && e.trigger === 'zoom') {
              const { selectedRange } = that.args;
              const { dateRange } = that;

              if (!dateRange) {
                return;
              }

              that.args.rangeChanged(dateRange);

              // `afterSetExtremes` fires twice for some reason. We don't want to send the usage tracking event twice,
              // so check that the date range hasn't been adjusted yet.
              // `.format()` is a quick fix because the dayjs objects have different properties (`offset` for example).
              if (
                dateRange.start.format('YYYY-MM-DD') !==
                  selectedRange.start.format('YYYY-MM-DD') ||
                dateRange.end.format('YYYY-MM-DD') !==
                  selectedRange.end.format('YYYY-MM-DD')
              ) {
                that.usageAnalytics.track('Stay Date Range Changed', {
                  eventTrigger: 'chart',
                  product: 'benchmark-insight',
                  amountOfDays: dateRange.end.diff(dateRange.start, 'days') + 1,
                  startDaysOffsetFromToday: dateRange.start.diff(
                    dayjs(),
                    'days',
                  ),
                });
              }
            }
          },
        },
      },
      // Add a second invisible xAxis, so we can show the reference period columns on this axis and make the columns overlap with the regular columns.
      {
        type: axisType,
        // Link this axis to the first X axis with linkedTo, to fix a bug where the axises would be out of sync
        // when selecting a date range by dragging the mouse across the chart.
        // To use linkedTo, the axis types also need to be the same.
        linkedTo: 0,
        visible: false,
      },
    ];

    return xAxis;
  }

  get yAxis(): Highcharts.YAxisOptions[] {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const gridLineColor = BENI_CHARTS_GRID_LINE_COLOR;
    const labelMinWidth = BENI_CHARTS_Y_AXIS_LABEL_MIN_WIDTH;
    const plotLines = [];

    if (this.args.selectedAxisOption === KPI_INDEXOPTION.INDEX) {
      plotLines.push(this.plotLineKpiIndex);
    }

    const yAxis = [
      {
        title: {
          text: this.TITLE_NAME, // Set title to empty string to remove the axis title, because we made a custom axis title
        },
        labels: {
          style: {
            color: BENI_CHARTS_LABEL_COLOR,
            minWidth: labelMinWidth,
          },
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            const value = this.value as number;

            return that.benchmarkInsightBeniChartService.yAxisLabelFormatter(
              value,
              that.args.isOccupancySelected,
            );
          },
        },
        tickAmount: this.TICK_AMOUNT,
        gridLineColor,
      },
      {
        title: {
          text: this.TITLE_NAME, // Set title to empty string to remove the axis title, because we made a custom axis title
        },
        opposite: true,
        // Use floor instead of min, because we want Highcharts to choose an appropriate min value, but never to go below 0.
        // This way Highcharts can still set it higher, if the values are way higher than 0.
        floor: 0,
        labels: {
          style: {
            color: BENI_CHARTS_LABEL_COLOR,
            minWidth: labelMinWidth,
          },
          formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
            const { isOccupancySelected, selectedAxisOption } = that.args;
            const value = this.value as number;

            if (
              !isOccupancySelected ||
              selectedAxisOption === KPI_INDEXOPTION.INDEX
            ) {
              const percentage = value * 100;
              // Use Math.round to round to one decimal place.
              const roundedValue = Math.round(percentage * 10) / 10;
              return selectedAxisOption === KPI_INDEXOPTION.INDEX
                ? `${roundedValue}`
                : `${roundedValue}%`;
            }

            return `${that.benchmarkInsightBeniService.formatNumber(
              value,
              NUMBER_FORMATS.CURRENCY,
              NUMBER_STYLES.DEFAULT,
            )}`;
          },
        },
        tickAmount: this.TICK_AMOUNT,
        gridLineColor,
        plotLines,
      },
    ];

    return yAxis;
  }

  willDestroy(): void {
    super.willDestroy();

    this.warningIconElement = null;
  }

  addComparisonSetBreakdownsToTooltipDataDict(
    dict: Record<number, BeniTooltipData>,
    comparisonSetMeta: BeniMetaComparisonSet,
    key:
      | 'ownComparisonSetBreakdown'
      | 'primaryComparisonSetBreakdown'
      | 'secondaryComparisonSetBreakdown',
  ): void {
    const { chartData, benchmarkInsightBeniChartService } = this;
    const { dateAggregation } = this.args;

    const comparisonSetSeries: HeroChartSeries | null =
      benchmarkInsightBeniChartService.getSeriesByComparisonSetType(
        chartData,
        comparisonSetMeta.comparisonSetType,
      );

    comparisonSetSeries?.data.forEach((comparisonSetSeriesPoint) => {
      const xValue = comparisonSetSeriesPoint.x;
      const comparisonSetBreakdown =
        comparisonSetSeriesPoint.segmentation ?? null;
      const existingDictEntry = dict[xValue];

      if (existingDictEntry) {
        existingDictEntry[key] = comparisonSetBreakdown;
      } else {
        const tooltipData: BeniTooltipData = {
          ownComparisonSetBreakdown: null,
          primaryComparisonSetBreakdown: null,
          secondaryComparisonSetBreakdown: null,
          date: dayjs.utc(xValue),
          dateAggregation,
          showWarningForWeek:
            comparisonSetSeriesPoint.showWarningForWeek ?? false,
        };
        tooltipData[key] = comparisonSetBreakdown;

        dict[xValue] = tooltipData;
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  addZoneIntersections(
    kpiDiffVsComparisonSetSeries: HeroChartSeries,
    kpiDiffVsComparisonSetTopBorderGhostSeries: HeroChartSeries,
    kpiDiffVsComparisonSetBottomBorderGhostSeries: HeroChartSeries,
    ownSegmentation: BeniBreakdown,
    xValueSegmentation: number,
    index: number,
    isLastZonePositive: boolean,
    previousDiffVsMarketOrCompsetPoint: HeroChartKpiDiffVsComparisonSetSeriesPoint | null,
  ) {
    const { primaryComparisonSetBreakdownsWithoutTotal } = this;
    const { selectedKpi } = this.args;

    let isLastZonePositiveForThisSegmentation = isLastZonePositive;

    const compareKpi =
      selectedKpi === KPIS.OCCUPANCY ? KPIS.ADR : KPIS.OCCUPANCY;
    const primaryComparisonSetSegmentation =
      primaryComparisonSetBreakdownsWithoutTotal.find(
        (comparisonSetSegmentation) =>
          comparisonSetSegmentation.label === ownSegmentation.label,
      );
    const kpiDiffVsComparisonSetPoint = {
      x: xValueSegmentation,
      high: ownSegmentation.current.total[compareKpi],
      low: primaryComparisonSetSegmentation?.current.total[compareKpi] ?? null,
    };

    let currentDifferenceIsPositive = false;
    const ownValue = ownSegmentation.current.total[compareKpi];
    const primaryComparisonSetValue =
      primaryComparisonSetSegmentation?.current.total[compareKpi] ?? null;
    if (ownValue !== null && primaryComparisonSetValue !== null) {
      const currentDifference = ownValue - primaryComparisonSetValue;
      currentDifferenceIsPositive = currentDifference >= 0;
    }

    // Add zone if the difference is positive and the last zone was negative or vice versa
    if (currentDifferenceIsPositive !== isLastZonePositive) {
      let newXValueSegmentation = xValueSegmentation;
      if (index > 0) {
        // We want to change color on the X value where the 2 lines intersect, instead of
        // the X value of the point.
        const intersectionPoint = intersection(
          0,
          kpiDiffVsComparisonSetPoint.high ?? 0,
          1,
          previousDiffVsMarketOrCompsetPoint?.high ?? 0,
          0,
          kpiDiffVsComparisonSetPoint.low ?? 0,
          1,
          previousDiffVsMarketOrCompsetPoint?.low ?? 0,
        );
        if (intersectionPoint) {
          // Intersection gives us a percentage of where the line intersects (e.g. 0.55).
          // So we check the distance between the 2 points, and multiply it by the
          // percentage, to get the distance in milliseconds (the unit of the axis).
          // Note: the percentage goes from right to left, so 0.90 is closer to the
          // previous point than to the current point.
          newXValueSegmentation =
            xValueSegmentation -
            (xValueSegmentation -
              (previousDiffVsMarketOrCompsetPoint?.x ?? 0)) *
              intersectionPoint.x;
        }
      }
      if (kpiDiffVsComparisonSetSeries.zones) {
        addZone(kpiDiffVsComparisonSetSeries.zones, newXValueSegmentation, {
          color: currentDifferenceIsPositive
            ? 'var(--prism-color-general-critical-100)'
            : 'var(--prism-color-general-success-100)',
        });
        isLastZonePositiveForThisSegmentation = currentDifferenceIsPositive;
      }
    }

    const previousKpiDiffVsComparisonSetPoint: HeroChartKpiDiffVsComparisonSetSeriesPoint =
      kpiDiffVsComparisonSetPoint;

    kpiDiffVsComparisonSetSeries.data.push(kpiDiffVsComparisonSetPoint);

    // We only display either of the borders if both can be shown for this kpiDiffVsComparisonSetPoint.
    // The area-range series, needs both the high and low to draw the area. We want to mimic this behaviour for the borders,
    // If not, we might encounter situations where only one border is displayed for some points, resulting in no area being rendered for those points.
    const { high, low } = kpiDiffVsComparisonSetPoint;
    const hasHighAndLow = high !== null && low !== null;
    kpiDiffVsComparisonSetTopBorderGhostSeries.data.push({
      x: xValueSegmentation,
      y: hasHighAndLow ? high : null,
    });
    kpiDiffVsComparisonSetBottomBorderGhostSeries.data.push({
      x: xValueSegmentation,
      y: hasHighAndLow ? low : null,
    });

    return {
      isLastZonePositiveForThisSegmentation,
      previousKpiDiffVsComparisonSetPoint,
    };
  }

  filterBreakdowns(breakdowns: BeniBreakdown[]): BeniBreakdown[] {
    return breakdowns.filter(
      (breakdown) => !breakdown.label.includes(BENI_TABLE_ROW_TYPES.TOTAL),
    );
  }

  // Returns an array of series objects, one series for the current period and one series for the reference period.
  getChartDataSeriesByComparisonSet(
    comparisonSetMeta: BeniMetaComparisonSet | null,
    seriesId: HeroChartSeriesId,
    referenceSeriesId: HeroChartSeriesId,
    color: string,
    referenceColor: string,
  ): HeroChartSeries[] {
    // If there's no comparisonSetType, intl will throw an error.
    if (!comparisonSetMeta) {
      return [];
    }

    const { comparisonSetType } = comparisonSetMeta;
    const { hasBenchmarkInsightReferencePeriodData } = this.access;

    // Due to how we show the reference period columns overlapping with the regular columns,
    // whenever there's at least 1 comparison set with reference period data, we need to show
    // the reference period columns for all comparison sets.
    // Otherwise the reference period columns won't be aligned nicely with the regular columns.
    const showAllComparisonSetReferencePeriodSeries =
      hasBenchmarkInsightReferencePeriodData &&
      this.comparisonSetsHaveReferencePeriodData;

    const comparisonSetHasReferencePeriodData =
      hasBenchmarkInsightReferencePeriodData &&
      comparisonSetMeta.hasReferencePeriodData;

    const POINT_PADDING_WITH_REFERENCE_PERIOD = 0.35;
    const POINT_PADDING_WITHOUT_REFERENCE_PERIOD = 0.1;

    const series: HeroChartSeries[] = [
      {
        name: this.intl.t(
          BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType],
        ),
        id: seriesId,
        data: [],
        color,
        visible: this.visibleSeries.includes(seriesId),
        zIndex: 2,
        xAxis: 0, // Show regular columns on the first X axis.
        // Make the regular columns smaller, so the reference columns are visible behind them.
        pointPadding: showAllComparisonSetReferencePeriodSeries
          ? POINT_PADDING_WITH_REFERENCE_PERIOD
          : POINT_PADDING_WITHOUT_REFERENCE_PERIOD,
        breakdownPeriodType: BREAKDOWN_PERIOD_TYPES.CURRENT,
        comparisonSetType,
        type: 'column',
      },
    ];

    if (showAllComparisonSetReferencePeriodSeries) {
      series.push({
        name: this.benchmarkInsightBeniService.getComparisonSetReferencePeriodShortTranslation(
          comparisonSetType,
          this.args.referencePeriod,
        ),
        id: referenceSeriesId,
        type: REFERENCE_PERIOD_COLUMN,
        data: [],
        color: referenceColor,
        visible: this.visibleSeries.includes(referenceSeriesId),
        zIndex: 1,
        xAxis: 1, // Show reference columns on the second X axis (behind the regular columns).
        pointPadding: POINT_PADDING_WITHOUT_REFERENCE_PERIOD,
        breakdownPeriodType: BREAKDOWN_PERIOD_TYPES.REFERENCE,
        hasReferencePeriodData: comparisonSetHasReferencePeriodData,
        comparisonSetType,
      });
    }

    return series;
  }

  getCustomLegendsByComparisonSet(
    comparisonSetMeta: BeniMetaComparisonSet,
    chartSeriesId: HeroChartSeriesId,
    referenceChartSeriesId: HeroChartSeriesId,
    cssSelector: string,
    cssSelectorReference: string,
  ): CustomLegend[] {
    const { comparisonSetType, hasReferencePeriodData, isVisible } =
      comparisonSetMeta;
    const { selectedRange, selectedKpi } = this.args;

    const customLegendType = 'dot-large';

    const isCurrentPeriodDisabled =
      this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
        comparisonSetType,
        selectedRange.start,
        selectedKpi,
      );

    const customLegends: CustomLegend[] = [
      {
        id: chartSeriesId,
        cssSelector,
        comparisonSetType,
        type: customLegendType,
        isHidden: !this.visibleSeries.includes(chartSeriesId),
        isDisabled: isCurrentPeriodDisabled || !isVisible,
        label: this.intl.t(
          BENI_COMPARISON_SET_LONG_TRANSLATIONS[comparisonSetType],
        ),
        breakdownPeriodType: BREAKDOWN_PERIOD_TYPES.CURRENT,
        isDisabledForHiddenFutureCompsetData: isCurrentPeriodDisabled,
        isDisabledForUnavailableComparisonSet: !isVisible,
      },
    ];

    if (
      this.access.hasBenchmarkInsightReferencePeriodData &&
      hasReferencePeriodData
    ) {
      const { referencePeriod } = this.args;

      const referencePeriodDateRange =
        this.benchmarkInsightBeniService.getReferencePeriodDateRange(
          referencePeriod,
          selectedRange,
        );
      const isReferencePeriodDisabled =
        this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
          comparisonSetType,
          referencePeriodDateRange.start,
          selectedKpi,
        );

      customLegends.push({
        id: referenceChartSeriesId,
        cssSelector: cssSelectorReference,
        comparisonSetType,
        type: customLegendType,
        isHidden: !this.visibleSeries.includes(referenceChartSeriesId),
        isDisabled: isReferencePeriodDisabled || !isVisible,
        label:
          this.benchmarkInsightBeniService.getComparisonSetReferencePeriodShortTranslation(
            comparisonSetType,
            this.args.referencePeriod,
          ),
        breakdownPeriodType: BREAKDOWN_PERIOD_TYPES.REFERENCE,
        isDisabledForHiddenFutureCompsetData: isReferencePeriodDisabled,
        isDisabledForUnavailableComparisonSet: !isVisible,
      });
    }

    return customLegends;
  }

  getXValueForSegmentation(segmentation: BeniBreakdown): number {
    return this.hotelAndMarketService.toLocalUnixTime(segmentation.date.unix());
  }

  shouldShowWarningForWeek(value: number): boolean {
    const { dateAggregation, selectedRange } = this.args;

    // Show a warning sign if the week is not fully included in the selected range.
    return (
      dateAggregation === DATE_AGGREGATION.WEEK &&
      (dayjs(value).isBefore(selectedRange.start, 'day') ||
        dayjs(value).add(6, 'days').isAfter(selectedRange.end, 'day'))
    );
  }

  @action
  setWarningIconElement(element: HTMLElement): void {
    this.warningIconElement = element;
  }

  @action
  toggleSeries(chartSeriesId: HeroChartSeriesId): void {
    const { visibleSeries } = this;

    if (visibleSeries.includes(chartSeriesId)) {
      this.visibleSeries = visibleSeries.filter(
        (seriesId) => seriesId !== chartSeriesId,
      );

      this.usageAnalytics.track('Data Series Hidden', {
        eventTrigger: 'performance-overview',
        product: 'benchmark-insight',
        series: chartSeriesId,
      });
    } else {
      this.visibleSeries = visibleSeries.concat(chartSeriesId);

      this.usageAnalytics.track('Data Series Shown', {
        eventTrigger: 'performance-overview',
        product: 'benchmark-insight',
        series: chartSeriesId,
      });
    }

    // Track the toggled on series in this format: { own: true, compset: true, kpi-diff-vs-comparison-set: true }.
    // Only add visible series to the object. Data Analytics team only wanted the 'true' values.
    const visibleSeriesJson: Partial<Record<HeroChartSeriesId, boolean>> = {};
    this.visibleSeries.forEach((seriesId) => {
      visibleSeriesJson[seriesId] = true;
    });
  }

  <template>
    <GeneralChartsOtaChart
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
    />

    <div hidden id={{this.TOOLTIP_DUMMY_ELEMENT_ID}}>
      {{#if this.tooltipData}}
        <HotelBenchmarkInsightTooltipsBeniTooltip
          @ownComparisonSetMeta={{@ownComparisonSetMeta}}
          @pickupTranslation={{@pickupTranslation}}
          @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
          @referencePeriod={{@referencePeriod}}
          @secondaryComparisonSetMeta={{@secondaryComparisonSetMeta}}
          @selectedKpi={{@selectedKpi}}
          @selectedRange={{@selectedRange}}
          @showMix={{false}}
          @tooltipData={{this.tooltipData}}
          @visibleKpis={{this.visibleKpis}}
        >
          <HotelBenchmarkInsightTooltipsSharedTooltipMetricBlock
            @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
            @referencePeriod={{@referencePeriod}}
            @selectedKpi={{@selectedKpi}}
            @tooltipData={{this.tooltipData}}
          />
        </HotelBenchmarkInsightTooltipsBeniTooltip>
      {{/if}}
    </div>
    <div hidden {{getElement this.setWarningIconElement}}>
      <PrismIcon @color="warning" @name="warning" @size="small" />
    </div>

    <div class="chart-legend">
      {{#each this.visibleCustomLegends as |legend|}}
        <HotelBenchmarkInsightChartsLegendItemsLegendItem
          @legend={{legend}}
          @toggleSeries={{this.toggleSeries}}
        />
      {{/each}}
    </div>
  </template>
}
